/**
 * Canonical plan ids. The same logical plan ships under an environment-prefixed id outside
 * prod (`ci_basic`, `uat_mid-annual`) and carries a billing cadence (`premium-annual`), so
 * compare against these rather than a raw `planId`.
 */
export const PLAN_IDS = {
    BASIC: 'basic',
    BUSINESS: 'business',
    BUSINESS_PLUS: 'businessplus',
    CUSTOM_PRICING: 'custom-pricing',
    DEVELOPER: 'developer',
    ENTERPRISE: 'enterprise',
    MID: 'mid',
    PLATFORM: 'platform',
    PREMIUM: 'premium',
    VANTA: 'vanta',
};

const KNOWN_PLAN_IDS = new Set(Object.values(PLAN_IDS));

const ENVIRONMENT_PREFIX = /^(?:ci|uat)_/;

// `-annual` / `-monthly` are billing cadence, not tier.
const BILLING_INTERVAL_SUFFIX = /-(?:annual|monthly)$/;

// `PLAT` exists alongside `platform` in uat and ci, but not in prod.
const PLAN_ID_ALIASES = {
    plat: PLAN_IDS.PLATFORM,
};

/**
 * Resolves a raw `subscription.planId` to a canonical entry in `PLAN_IDS`.
 *
 * @param {string} [planId] Raw plan id as the customer payload carries it.
 * @returns {string|null} A `PLAN_IDS` value, or null when the id isn't one we know.
 */
export const normalizePlanId = planId => {
    if (typeof planId !== 'string') {
        return null;
    }

    const trimmed = planId.trim().toLowerCase();

    if (!trimmed) {
        return null;
    }

    const withoutEnvironmentPrefix = trimmed.replace(ENVIRONMENT_PREFIX, '');
    const tier = withoutEnvironmentPrefix.replace(BILLING_INTERVAL_SUFFIX, '');
    const resolved = PLAN_ID_ALIASES[tier] ?? tier;

    return KNOWN_PLAN_IDS.has(resolved) ? resolved : null;
};
